"use client"

// Root error boundary — the last line of defence (FRIDGE-177).
//
// This catches errors thrown by the ROOT LAYOUT itself, which app/error.tsx
// cannot: error.tsx renders *inside* the layout, so if the layout is what
// failed, there is nothing left to render it in. That is why this file must
// supply its own <html> and <body>.
//
// It is also the file Sentry's build warning names explicitly — without it,
// React render errors never reach Sentry regardless of how completely the SDK
// is configured elsewhere.
//
// ── WHY INLINE STYLES, NOT TAILWIND ────────────────────────────────────────
// Deliberate, and the one place in this codebase where it is right. The root
// layout has just failed. Assuming the stylesheet pipeline, the font loader,
// the theme provider and the component library are all still intact would be
// assuming the very thing that is in doubt — and a fallback that depends on the
// thing it is falling back from is not a fallback.
//
// Inline styles need nothing but the browser. Light palette only, per the
// standing output rule: never dark mode.
//
// ── NO SiteHeader / SiteFooter HERE, ALSO DELIBERATE ────────────────────────
// Both pull in providers, navigation data and the component library. Rendering
// them at the point the layout has already thrown risks a second error inside
// the error boundary, which produces the blank white screen this file exists to
// prevent. app/error.tsx keeps the full branded chrome because by then the
// layout is known-good; this one trades chrome for certainty.

import { useEffect } from 'react'

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Loaded on demand: the monitoring SDK is fetched after consent by
    // instrumentation-client.ts, and this resolves to that same module. A static
    // import here put the SDK core in every route's first-load script.
    void import('@sentry/nextjs')
      .then((Sentry) => { Sentry.captureException(error) })
      .catch(() => { /* reporting is best-effort */ })
  }, [error])

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ffffff',
          color: '#111827',
          fontFamily:
            'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          padding: '24px',
        }}
      >
        <main style={{ maxWidth: '32rem', textAlign: 'center' }}>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 700, margin: '0 0 1rem', letterSpacing: '-0.025em' }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: '1.0625rem', lineHeight: 1.6, color: '#4b5563', margin: '0 0 2rem' }}>
            We hit an unexpected problem loading this page. Our team has already
            been alerted. Please try again.
          </p>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={reset}
              style={{
                appearance: 'none',
                border: '1px solid #111827',
                background: '#111827',
                color: '#ffffff',
                borderRadius: '8px',
                padding: '12px 20px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- deliberate: recovery from a root-layout crash must be a full document load. <Link> would soft-navigate back into the same React tree that just threw, and this file's whole contract (see header) is to depend on nothing but the browser. */}
            <a
              href="/"
              style={{
                display: 'inline-block',
                border: '1px solid #d1d5db',
                background: '#ffffff',
                color: '#111827',
                borderRadius: '8px',
                padding: '12px 20px',
                fontSize: '1rem',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Go to homepage
            </a>
          </div>

          {/* `digest` is an opaque identifier Next.js assigns, safe to display, and the value
              that ties a user report to the Sentry event. `error.message` is
              deliberately never rendered. */}
          {error.digest ? (
            <p style={{ marginTop: '2.5rem', fontSize: '0.875rem', color: '#9ca3af' }}>
              Reference:{' '}
              <code style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                {error.digest}
              </code>
            </p>
          ) : null}
        </main>
      </body>
    </html>
  )
}
